import { useState, type FormEvent } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useRessource } from '../../hooks/useRessource';
import { ErreurApi, api } from '../../lib/apiClient';
import { AideEnBulle, Badge, Champ, Chargement, EnTetePage, Erreur, Infobulle } from '../../components/Elements';
import { LogoEntite } from '../../components/LogoEntite';
import { ChampTelephone } from '../../components/ChampTelephone';
import { VariablesPaie } from '../../components/VariablesPaie';
import { ArchivageEntite } from '../../components/ArchivageEntite';
import { TypesDocuments } from '../../components/TypesDocuments';
import { ComptesBancaires } from '../../components/ComptesBancaires';
import { telephonePourSaisie } from '../../lib/telephone';
import type { Entite } from '../../types';

/** Doit rester aligne sur TAILLE_MAX_OCTETS du service de stockage. */
const TAILLE_MAX_KIO = 512;

/**
 * Ecran Entites (2026-09-30) : une ecole a la fois, choisie en haut, et
 * son detail en blocs sur toute la page — la liste et sa modale ont
 * disparu. La liste deroulante s'ouvre sur l'entite active de la
 * session ; « Nouvelle entite » ouvre un detail vide.
 *
 * Deux regimes d'enregistrement, comme avant : les blocs de saisie
 * s'enregistrent ensemble par le bouton du bas ; le logo et les
 * variables de paie ecrivent immediatement, chaque geste etant un acte
 * a part.
 */
export function EntitesPage() {
  const { peut, entiteActive } = useAuth();
  const entites = useRessource(() => api.get<Entite[]>('/api/admin/entites'), []);

  // `null` : pas encore choisi, on suit l'entite active. `'nouvelle'` :
  // le detail vide. Sinon l'identifiant de l'entite regardee.
  const [choix, setChoix] = useState<string | null>(null);
  const liste = entites.donnees ?? [];
  const selectionnee = choix ?? entiteActive?.id ?? liste[0]?.id ?? null;
  const entite = selectionnee === 'nouvelle' ? null : (liste.find((e) => e.id === selectionnee) ?? null);

  return (
    <>
      <EnTetePage
        titre={
          <>
            Entités
            <AideEnBulle texte="Les entités sont exploitées dans la même instance, avec cloisonnement strict de leurs données. La couleur sert à identifier l’entité active sur chaque écran." />
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {/* La pastille suit l'ecole choisie : le meme repere que le bandeau. */}
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: entite?.couleur_theme ?? '#c5c7cc' }}
              aria-hidden
            />
            <select
              className="champ w-auto min-w-[16rem]"
              aria-label="Entité affichée"
              value={selectionnee ?? ''}
              onChange={(e) => setChoix(e.target.value)}
            >
              {liste
                .filter((e) => e.actif)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} — {e.raison_sociale}
                  </option>
                ))}
              {liste.some((e) => !e.actif) && (
                <optgroup label="Désactivées">
                  {liste
                    .filter((e) => !e.actif)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.code} — {e.raison_sociale}
                      </option>
                    ))}
                </optgroup>
              )}
              {selectionnee === 'nouvelle' && <option value="nouvelle">Nouvelle entité</option>}
            </select>
            {peut('entite.creer') && selectionnee !== 'nouvelle' && (
              <button type="button" className="bouton-principal" onClick={() => setChoix('nouvelle')}>
                Nouvelle entité
              </button>
            )}
          </div>
        }
      />

      {entites.erreur && <Erreur message={entites.erreur} onReessayer={entites.recharger} />}
      {entites.chargement ? (
        <Chargement />
      ) : (
        <DetailEntite
          key={selectionnee ?? 'vide'}
          entite={entite}
          peutModifier={selectionnee === 'nouvelle' ? peut('entite.creer') : peut('entite.modifier')}
          onEnregistre={(sauvee) => {
            setChoix(sauvee.id);
            entites.recharger();
          }}
          onAnnulerCreation={() => setChoix(entiteActive?.id ?? liste[0]?.id ?? null)}
          onChange={entites.recharger}
        />
      )}
    </>
  );
}

/**
 * Le bandeau : logo (cliquable pour le remplacer), statut, adresse et
 * volumetrie, « Desactiver » a droite. La volumetrie est affichee
 * D'EMBLEE : on voit ce qu'on desactive avant de cliquer (RG-34), la
 * fenetre de confirmation ne fait que le redire.
 */
function BandeauEntite({ entite, onChange }: { entite: Entite; onChange: () => void }) {
  const { peut } = useAuth();
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [version, setVersion] = useState(Date.now());

  const volumetrie = useRessource(
    () =>
      api.get<{ activites: number; inscriptions: number; habilitations: number }>(
        `/api/admin/entites/${entite.id}/volumetrie`,
      ),
    [entite.id],
  );

  const garder = async (travail: () => Promise<unknown>) => {
    setErreur(null);
    setEnCours(true);
    try {
      await travail();
      setVersion(Date.now());
      onChange();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Action impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const envoyerLogo = (fichier: File) => {
    // Controle cote client pour un retour immediat ; le serveur refait le
    // sien, qui seul fait foi.
    if (fichier.size > TAILLE_MAX_KIO * 1024) {
      setErreur(`Fichier trop volumineux (${Math.round(fichier.size / 1024)} Kio). Maximum : ${TAILLE_MAX_KIO} Kio.`);
      return;
    }
    void garder(() => api.televerser<Entite>(`/api/admin/entites/${entite.id}/logo`, fichier));
  };

  const basculer = () => {
    const v = volumetrie.donnees;
    if (entite.actif) {
      const confirme = window.confirm(
        `Désactiver ${entite.code} ?\n\n` +
          (v
            ? `Cette entité porte ${v.activites} activité(s), ${v.inscriptions} inscription(s) et ${v.habilitations} habilitation(s).\n\n`
            : '') +
          `Les données sont conservées ; l’entité disparaît simplement des sélecteurs.`,
      );
      if (!confirme) return;
    }
    void garder(() => api.patch(`/api/admin/entites/${entite.id}/activation`, { actif: !entite.actif }));
  };

  const adresse = [entite.adresse, entite.code_postal, entite.ville].filter(Boolean).join(', ');
  const v = volumetrie.donnees;

  return (
    <div className="carte mb-4 flex flex-wrap items-center gap-4 p-4">
      {/* Le logo se remplace en cliquant la vignette. Le SVG n'est pas
          propose : il peut embarquer du script, et le serveur le refuse
          de toute facon. */}
      <Infobulle
        balise="label"
        texte={peut('entite.modifier') ? `Remplacer le logo — PNG, JPEG ou WebP, ${TAILLE_MAX_KIO} Kio maximum` : null}
        className={`flex h-16 w-24 shrink-0 items-center justify-center rounded border border-encre-200 bg-white p-1 ${
          peut('entite.modifier') ? 'cursor-pointer hover:border-encre-400' : ''
        }`}
      >
        <LogoEntite entite={entite} className="max-h-full max-w-full" version={version} />
        {peut('entite.modifier') && (
          <input
            type="file"
            className="hidden"
            accept="image/png,image/jpeg,image/webp"
            disabled={enCours}
            onChange={(evenement) => {
              const fichier = evenement.target.files?.[0];
              // Reinitialise pour que rechoisir le meme fichier renvoie.
              evenement.target.value = '';
              if (fichier) envoyerLogo(fichier);
            }}
          />
        )}
      </Infobulle>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold">{entite.raison_sociale}</span>
          <Badge ton={entite.actif ? 'succes' : 'neutre'}>{entite.actif ? 'Active' : 'Désactivée'}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-encre-500">
          <span className="font-medium text-encre-700">{entite.code}</span>
          {adresse ? ` · ${adresse}` : ''}
          {v ? ` · ${v.activites} activité(s), ${v.inscriptions} inscription(s), ${v.habilitations} habilitation(s)` : ''}
        </p>
        <p className="mt-0.5 text-xs text-encre-400">
          {entite.logo ? (
            peut('entite.modifier') ? (
              <button
                type="button"
                className="underline"
                disabled={enCours}
                onClick={() => void garder(() => api.delete<Entite>(`/api/admin/entites/${entite.id}/logo`))}
              >
                Retirer le logo
              </button>
            ) : null
          ) : (
            'Sans logo propre, le logo Artistic est affiché.'
          )}
        </p>
        {erreur && <p className="mt-1 text-xs text-red-700">{erreur}</p>}
      </div>

      {peut('entite.desactiver') && (
        <button
          type="button"
          className={entite.actif ? 'bouton-danger py-1.5' : 'bouton-secondaire py-1.5'}
          disabled={enCours}
          onClick={basculer}
        >
          {entite.actif ? 'Désactiver' : 'Réactiver'}
        </button>
      )}
    </div>
  );
}

function Bloc({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <fieldset className="carte space-y-3 p-4">
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">{titre}</legend>
      {children}
    </fieldset>
  );
}

function DetailEntite({
  entite,
  peutModifier,
  onEnregistre,
  onAnnulerCreation,
  onChange,
}: {
  entite: Entite | null;
  peutModifier: boolean;
  onEnregistre: (entite: Entite) => void;
  onAnnulerCreation: () => void;
  onChange: () => void;
}) {
  // Les variables de paie se reglent avec la permission de la paie, pas
  // celle de l'entite : le bloc lit le droit lui-meme.
  const { peut } = useAuth();
  const initiales = () => ({
    code: entite?.code ?? '',
    raisonSociale: entite?.raison_sociale ?? '',
    formeJuridique: entite?.forme_juridique ?? 'Association loi 1901',
    siret: entite?.siret ?? '',
    naf: entite?.naf ?? '',
    numeroGuso: entite?.numero_guso ?? '',
    conventionCollective: entite?.convention_collective ?? '',
    idcc: entite?.idcc ?? '',
    adresse: entite?.adresse ?? '',
    codePostal: entite?.code_postal ?? '',
    ville: entite?.ville ?? '',
    email: entite?.email ?? '',
    telephone: telephonePourSaisie(entite?.telephone, entite?.telephone_pays),
    telephonePays: entite?.telephone_pays ?? 'FR',
    siteWeb: entite?.site_web ?? '',
    agrementJep: entite?.agrement_jep ?? '',
    couleurTheme: entite?.couleur_theme ?? '#5b5f6a',
    inscriptionEnLigneDebut: entite?.inscription_en_ligne_debut ?? '',
    inscriptionEnLigneFin: entite?.inscription_en_ligne_fin ?? '',
    inscriptionEnLigneMessage: entite?.inscription_en_ligne_message ?? '',
    urlCharte: entite?.url_charte ?? '',
    urlStatuts: entite?.url_statuts ?? '',
    driveId: entite?.drive_id ?? '',
  });
  // Le composant est recree (`key`) a chaque changement d'entite : l'etat
  // part donc toujours de la bonne fiche.
  const [valeurs, setValeurs] = useState(initiales);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const modifie = JSON.stringify(valeurs) !== JSON.stringify(initiales());

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        code: valeurs.code,
        raisonSociale: valeurs.raisonSociale,
        formeJuridique: valeurs.formeJuridique || null,
        siret: valeurs.siret || null,
        naf: valeurs.naf || null,
        numeroGuso: valeurs.numeroGuso || null,
        conventionCollective: valeurs.conventionCollective || null,
        idcc: valeurs.idcc || null,
        adresse: valeurs.adresse || null,
        codePostal: valeurs.codePostal || null,
        ville: valeurs.ville || null,
        email: valeurs.email || null,
        telephone: valeurs.telephone || null,
        telephonePays: valeurs.telephonePays,
        siteWeb: valeurs.siteWeb || null,
        agrementJep: valeurs.agrementJep || null,
        couleurTheme: valeurs.couleurTheme,
        inscriptionEnLigneDebut: valeurs.inscriptionEnLigneDebut || null,
        inscriptionEnLigneFin: valeurs.inscriptionEnLigneFin || null,
        inscriptionEnLigneMessage: valeurs.inscriptionEnLigneMessage || null,
        urlCharte: valeurs.urlCharte || null,
        urlStatuts: valeurs.urlStatuts || null,
        driveId: valeurs.driveId || null,
      };
      // Le code ne part qu'a la creation : la modification ne le touche pas.
      const sauvee = entite
        ? await api.patch<Entite>(`/api/admin/entites/${entite.id}`, { ...corps, code: undefined })
        : await api.post<Entite>('/api/admin/entites', corps);
      onEnregistre(sauvee);
    } catch (cause) {
      // Une saisie refusee par la validation porte le detail par champ
      // (« Code IDCC attendu sur 4 chiffres ») : c'est lui qu'on montre,
      // pas le generique « Les donnees transmises sont invalides » qui
      // laissait deviner quel champ etait en cause (2026-10-30).
      setErreur(
        cause instanceof ErreurApi
          ? (cause.details?.map((d) => d.message).join(' · ') || cause.message)
          : 'Enregistrement impossible.',
      );
    } finally {
      setEnCours(false);
    }
  };

  const champ = (cle: keyof ReturnType<typeof initiales>) => ({
    value: valeurs[cle],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setValeurs({ ...valeurs, [cle]: e.target.value }),
    disabled: !peutModifier,
  });

  return (
    <>
      {entite && <BandeauEntite entite={entite} onChange={onChange} />}

      <form onSubmit={soumettre} className="space-y-4">
        <Bloc titre="Identité">
          {/* TROIS LIGNES SUR HUIT COLONNES (2026-10-13, remaniees le
              2026-10-30) : code 2/8, raison sociale 4/8, forme 2/8 ;
              SIRET 2/8, NAF 1/8, IDCC 1/8, convention 4/8 ; GUSO 2/8,
              agrement JEP 2/8 (2026-11-03) et couleur 4/8. Sous `lg`, deux colonnes. */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-8">
            <div className="lg:col-span-2">
              {/* Le code se fixe a la creation et ne se modifie plus a
                  l'ecran (2026-10-30) : il est l'adresse du formulaire
                  public et le prefixe des documents archives. */}
              <Champ libelle="Code" aide={entite ? undefined : 'Majuscules, chiffres et tirets bas.'}>
                <input
                  className="champ font-mono uppercase"
                  {...champ('code')}
                  onChange={(e) => setValeurs({ ...valeurs, code: e.target.value.toUpperCase() })}
                  disabled={!peutModifier || entite !== null}
                  required
                  autoFocus={!entite}
                />
              </Champ>
            </div>
            <div className="lg:col-span-4">
              <Champ libelle="Raison sociale">
                <input className="champ" {...champ('raisonSociale')} required />
              </Champ>
            </div>
            <div className="lg:col-span-2">
              <Champ libelle="Forme juridique">
                <input className="champ" {...champ('formeJuridique')} />
              </Champ>
            </div>

            <div className="lg:col-span-2">
              <Champ libelle="SIRET">
                <input className="champ tabulaire" {...champ('siret')} inputMode="numeric" maxLength={14} />
              </Champ>
            </div>
            {/* Le code NAF (APE) de l'employeur (2026-10-30) : quatre
                chiffres et une lettre, 8552Z pour l'enseignement culturel.
                Le serveur accepte le point de l'INSEE et le retire. */}
            <div className="lg:col-span-1">
              <Champ libelle="Code NAF" infobulle="Quatre chiffres et une lettre, 8552Z par exemple.">
                <input
                  className="champ tabulaire uppercase"
                  {...champ('naf')}
                  onChange={(e) => setValeurs({ ...valeurs, naf: e.target.value.toUpperCase() })}
                  maxLength={6}
                />
              </Champ>
            </div>
            {/* La convention collective de l'employeur (2026-10-13) : le
                code IDCC, celui qui figure sur le bulletin, et le libelle.
                Les valeurs conventionnelles de la paie, elles, vivent
                dans les variables de paie. */}
            <div className="lg:col-span-1">
              <Champ libelle="Code IDCC">
                <input className="champ tabulaire" {...champ('idcc')} inputMode="numeric" maxLength={4} />
              </Champ>
            </div>
            <div className="lg:col-span-4">
              <Champ
                libelle="Convention collective"
                infobulle="Celle qui s’applique aux contrats de l’école, telle qu’elle figure sur les bulletins."
              >
                <input className="champ" {...champ('conventionCollective')} maxLength={200} />
              </Champ>
            </div>

            <div className="lg:col-span-2">
              <Champ
                libelle="N° GUSO"
                infobulle="Immatriculation de l’employeur au GUSO, qui porte les déclarations de cachets."
              >
                <input className="champ tabulaire" {...champ('numeroGuso')} maxLength={32} />
              </Champ>
            </div>
            {/* L'agrement Jeunesse et Education Populaire (2026-11-03) :
                texte libre, imprime au pied de l'attestation de paiement
                a cote du SIRET. */}
            <div className="lg:col-span-2">
              <Champ
                libelle="Agrément JEP"
                infobulle="Numéro d’agrément Jeunesse et Éducation Populaire, tel qu’il figure sur l’arrêté (75 JEP 11-9). Imprimé au pied de l’attestation de paiement."
              >
                <input className="champ tabulaire" {...champ('agrementJep')} maxLength={200} />
              </Champ>
            </div>
            <div className="lg:col-span-4">
              {/* L'explication passe en infobulle du libelle (2026-10-30) :
                  on ne la relit pas a chaque fois qu'on ouvre la fiche. */}
              <Champ
                libelle="Couleur d’identification"
                infobulle="Rappelée par la pastille de l’en-tête : c’est ce qui évite de saisir dans la mauvaise entité."
              >
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    className="h-10 w-16 cursor-pointer rounded border border-encre-200"
                    value={valeurs.couleurTheme}
                    disabled={!peutModifier}
                    onChange={(e) => setValeurs({ ...valeurs, couleurTheme: e.target.value })}
                  />
                  <span className="tabulaire text-sm text-encre-500">{valeurs.couleurTheme}</span>
                </div>
              </Champ>
            </div>
          </div>
        </Bloc>

        <Bloc titre="Adresse et coordonnées">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <Champ libelle="Adresse">
                <input className="champ" {...champ('adresse')} />
              </Champ>
            </div>
            <Champ libelle="Code postal">
              <input className="champ tabulaire" {...champ('codePostal')} maxLength={5} />
            </Champ>
            <Champ libelle="Ville">
              <input className="champ" {...champ('ville')} />
            </Champ>
            <div className="lg:col-span-2">
              <Champ libelle="Email de contact">
                <input className="champ" type="email" {...champ('email')} />
              </Champ>
            </div>
            <div className="lg:col-span-2">
              <ChampTelephone
                numero={valeurs.telephone}
                pays={valeurs.telephonePays}
                onChange={(telephone, telephonePays) => setValeurs({ ...valeurs, telephone, telephonePays })}
              />
            </div>
            {/* Le site de l'ecole (2026-10-30) : une adresse complete, la
                validation la refuse sans http(s). */}
            <div className="lg:col-span-2">
              <Champ libelle="Site web">
                <input className="champ" type="url" placeholder="https://…" {...champ('siteWeb')} />
              </Champ>
            </div>
          </div>
        </Bloc>

        {/* Les comptes bancaires de l'ecole (2026-10-17) : la table
            existait, le bordereau l'imprimait, rien ne la reglait. Le bloc
            ecrit tout seul et n'existe qu'une fois l'entite creee. */}
        <Bloc titre="Coordonnées bancaires">
          {entite ? (
            <ComptesBancaires entiteId={entite.id} peutModifier={peutModifier} />
          ) : (
            <p className="text-xs text-encre-500">Les coordonnées bancaires se règlent une fois l’école créée.</p>
          )}
        </Bloc>

        {/* Les reglages du formulaire public d'inscription (2026-09-24) :
            la fenetre pendant laquelle il accepte des demandes, son
            message d'accueil, et les deux liens vers le site. */}
        <Bloc titre="Inscription en ligne">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Champ libelle="Ouverte du">
              <input type="date" className="champ" {...champ('inscriptionEnLigneDebut')} />
            </Champ>
            <Champ libelle="au">
              <input type="date" className="champ" {...champ('inscriptionEnLigneFin')} />
            </Champ>
            <div className="lg:col-span-2">
              <Champ libelle="Message d’accueil" aide="Affiché sous le titre du formulaire. Vide : le texte standard.">
                <input className="champ" {...champ('inscriptionEnLigneMessage')} />
              </Champ>
            </div>
            <div className="lg:col-span-2">
              <Champ libelle="Adresse de la charte du choriste">
                <input className="champ" type="url" placeholder="https://…" {...champ('urlCharte')} />
              </Champ>
            </div>
            <div className="lg:col-span-2">
              <Champ libelle="Adresse des statuts">
                <input className="champ" type="url" placeholder="https://…" {...champ('urlStatuts')} />
              </Champ>
            </div>
          </div>
          <p className="text-xs text-encre-500">
            Le formulaire est à l’adresse{' '}
            <span className="tabulaire">/inscription/{valeurs.code || 'CODE'}</span>. Il ne propose que
            les activités cochées « inscription en ligne ».
          </p>
        </Bloc>

        {/* Documents et archivage (ADR-0014, etape 3) : le Drive partage de
            l'ecole, ou les bordereaux partent une fois deposes. */}
        <Bloc titre="Documents et archivage">
          <div className="grid gap-3 sm:grid-cols-2">
            <Champ
              libelle="Identifiant du Drive partagé"
              aide="Ce qui suit /folders/ dans l’adresse du Drive. Le compte technique doit y être Contributeur."
            >
              <input className="champ tabulaire" placeholder="0ABcD…" {...champ('driveId')} />
            </Champ>
          </div>
          {entite && (
            <ArchivageEntite entiteId={entite.id} driveIdEnregistre={entite.drive_id} peutTester={peutModifier} />
          )}
          {entite && <TypesDocuments entiteId={entite.id} peutModifier={peutModifier} />}
          {!entite && (
            <p className="text-xs text-encre-500">
              Les types de documents se règlent une fois l’école créée.
            </p>
          )}
        </Bloc>

        {/* Les variables de paie de l'ecole (2026-09-30) : mutuelle et
            valeur du point, par periode. Le bloc ecrit tout seul et
            n'existe qu'une fois l'entite creee. */}
        {entite && <VariablesPaie entiteId={entite.id} peutGerer={peut('contrat.gerer')} />}

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" role="alert">
            {erreur}
          </p>
        )}

        {peutModifier && (
          <div className="flex items-center justify-end gap-2">
            {entite ? (
              <button
                type="button"
                className="bouton-secondaire"
                disabled={!modifie || enCours}
                onClick={() => setValeurs(initiales())}
              >
                Annuler les modifications
              </button>
            ) : (
              <button type="button" className="bouton-secondaire" onClick={onAnnulerCreation}>
                Annuler
              </button>
            )}
            <button type="submit" className="bouton-principal" disabled={enCours || (entite !== null && !modifie)}>
              {entite ? 'Enregistrer' : 'Créer l’entité'}
            </button>
          </div>
        )}
      </form>
    </>
  );
}
